import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronRight, Search, Terminal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { COMMANDS, GUIDES } from "@/lib/docsContent";

const Guide = ({ g }) => (
  <section id={g.id} data-testid={`docs-guide-${g.id}`} className="scroll-mt-24 rounded-sm border border-[#1E2D4A] bg-[#0B1120]/90 p-6">
    <div className="flex items-center justify-between gap-3">
      <h2 className="font-display text-lg font-bold uppercase tracking-wider text-slate-100">{g.title}</h2>
      {g.page && <Link data-testid={`docs-open-${g.id}`} to={g.page} className="flex items-center gap-1 text-xs text-[#E5202E] hover:text-red-300">Open page<ChevronRight className="h-3 w-3" /></Link>}
    </div>
    <ol className="mt-4 space-y-3">{g.steps.map(([t, d], i) => (
      <li key={t} className="flex gap-3 text-sm"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-[#E5202E]/15 font-mono text-[11px] text-[#E5202E]">{i + 1}</span>
        <div><div className="font-semibold text-slate-200">{t}</div><p className="text-slate-400">{d}</p></div></li>))}</ol>
  </section>
);

const CommandRow = ({ c }) => {
  const [name, args, desc, example, who] = c;
  return (
    <div data-testid={`docs-cmd-${name.replace(/[/ ]+/g, "-").replace(/^-/, "")}`} className="grid gap-1 border-t border-[#1E2D4A] py-2.5 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] sm:gap-4">
      <div className="break-words font-mono text-sm text-[#E5202E]">{name} <span className="text-slate-500">{args}</span></div>
      <div className="text-sm text-slate-300">{desc}<div className="mt-1 break-words font-mono text-xs text-slate-500">e.g. {example}</div></div>
      <div className="sm:text-right"><span className={`rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${who === "Staff" ? "bg-amber-400/15 text-amber-300" : "bg-emerald-400/10 text-emerald-300"}`}>{who}</span></div>
    </div>
  );
};

const Commands = () => {
  const [q, setQ] = useState("");
  const groups = useMemo(() => COMMANDS.map((g) => ({ ...g, items: g.items.filter((c) => `${c[0]} ${c[2]}`.toLowerCase().includes(q.toLowerCase())) })).filter((g) => g.items.length), [q]);
  return (
    <section id="commands" data-testid="docs-commands" className="scroll-mt-24 rounded-sm border border-[#1E2D4A] bg-[#0B1120]/90 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wider text-slate-100"><Terminal className="h-5 w-5 text-[#E5202E]" />Discord slash commands</h2>
        <div className="relative w-full sm:w-64"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" /><Input data-testid="docs-cmd-search" placeholder="Search commands" value={q} onChange={(e) => setQ(e.target.value)} className="border-[#1E2D4A] bg-[#050811] pl-9" /></div>
      </div>
      <p className="mt-2 text-sm text-slate-400">Type <code className="text-[#E5202E]">/</code> in your community's Discord and pick a command. Discord shows each option as you type. Replies only you can see are marked "Only you can see this". Most player commands need a linked account (<code className="text-[#E5202E]">/link</code>). Staff commands need a mod role set on the Discord Tools page.</p>
      {groups.length === 0 ? <p data-testid="docs-cmd-empty" className="mt-6 text-sm text-slate-500">No commands match.</p> : groups.map((g) => (
        <div key={g.group} className="mt-6">
          <div className="rg-label mb-1 text-[#E5202E]/80">{g.group}</div>
          <div>{g.items.map((c) => <CommandRow key={c[0]} c={c} />)}</div>
        </div>))}
    </section>
  );
};

export default function Docs() {
  const { session } = useAuth();
  return (
    <div className="min-h-screen bg-[#050811] rg-grid-bg">
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-[#1E2D4A] bg-[#070A14]/90 px-6 py-3 backdrop-blur-md">
        <div className="flex items-center gap-3"><img src="/ranger-logo.jpg" alt="RANGER" className="h-9 w-9 rounded-sm" /><span className="hidden font-display text-xl font-bold uppercase tracking-[0.25em] text-slate-100 sm:inline">Ranger</span><span className="rg-label">Docs</span></div>
        <div className="flex gap-4 whitespace-nowrap text-xs">
          <Link data-testid="docs-browse-link" to="/browse" className="text-slate-300 hover:text-white">Server list</Link>
          <Link data-testid="docs-app-link" to={session ? "/overview" : "/login"} className="text-slate-300 hover:text-white">{session ? "Back to dashboard" : "Sign in"}</Link>
        </div>
      </header>
      <main className="mx-auto grid max-w-7xl gap-8 px-4 py-10 lg:grid-cols-[220px_1fr]">
        <nav data-testid="docs-toc" className="hidden lg:block"><div className="sticky top-24 space-y-1 text-sm">
          <div className="rg-label mb-2">On this page</div>
          {GUIDES.map((g) => <a key={g.id} href={`#${g.id}`} className="block rounded-sm px-2 py-1 text-slate-400 transition-colors hover:bg-[#111A2E] hover:text-white">{g.title}</a>)}
          <a href="#commands" className="block rounded-sm px-2 py-1 font-semibold text-[#E5202E] hover:bg-[#111A2E]">Slash commands</a>
        </div></nav>
        <div className="min-w-0 space-y-6">
          <div><div className="rg-label text-[#E5202E]/80">RANGER guide</div>
            <h1 className="mt-2 flex items-center gap-3 text-4xl font-bold uppercase tracking-wide text-slate-100 sm:text-5xl"><BookOpen className="h-9 w-9 text-[#E5202E]" />Documentation</h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-400">How to set up every RANGER feature for your Rust Console community, plus every Discord slash command and how to use it.</p>
            <a href="#commands" data-testid="docs-jump-commands" className="mt-4 inline-flex items-center gap-1 text-sm text-[#E5202E] hover:text-red-300">Jump to slash commands<ChevronRight className="h-4 w-4" /></a></div>
          {GUIDES.map((g) => <Guide key={g.id} g={g} />)}
          <Commands />
        </div>
      </main>
    </div>
  );
}
